import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { login, isAuthenticated } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Lock, User2 } from "lucide-react";

export default function AdminLogin() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "BluBridge · Admin";
    }, []);

    if (isAuthenticated()) {
        return <Navigate to="/bbrgadminacc/modules" replace />;
    }

    const onSubmit = (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        const res = login(username.trim(), password);
        if (res.ok) {
            toast.success("Welcome back, Admin");
            navigate("/bbrgadminacc/modules", { replace: true });
        } else {
            setError(res.error);
            toast.error(res.error);
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="admin-login-page"
            className="min-h-screen bg-[#F7F5F0] text-[#0F1F38] font-sans relative"
        >
            {/* Logo top-left */}
            <header className="absolute top-0 inset-x-0 px-6 md:px-10 py-6">
                <img
                    src="/blubridge-logo.png"
                    alt="BluBridge"
                    data-testid="admin-login-logo"
                    className="h-6 md:h-7 w-auto"
                />
            </header>

            <main className="min-h-screen flex items-center justify-center px-6 py-24">
                <form
                    onSubmit={onSubmit}
                    data-testid="admin-login-form"
                    className="w-full max-w-md"
                >
                    <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/50 mb-3 text-center">
                        Sign in
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl leading-[1] mb-10 text-center">
                        Welcome back.
                    </h1>

                    <div className="space-y-5">
                        <div>
                            <Label
                                htmlFor="admin-username"
                                className="text-[10px] tracking-label uppercase text-[#0F1F38]/60"
                            >
                                Username
                            </Label>
                            <div className="mt-2 relative">
                                <User2
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F1F38]/40"
                                />
                                <Input
                                    id="admin-username"
                                    data-testid="admin-login-username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="admin"
                                    autoComplete="username"
                                    required
                                    className="pl-10 h-12 rounded-none border-[#0F1F38]/25 bg-white focus-visible:ring-[#0F1F38] focus-visible:ring-offset-0"
                                />
                            </div>
                        </div>

                        <div>
                            <Label
                                htmlFor="admin-password"
                                className="text-[10px] tracking-label uppercase text-[#0F1F38]/60"
                            >
                                Password
                            </Label>
                            <div className="mt-2 relative">
                                <Lock
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F1F38]/40"
                                />
                                <Input
                                    id="admin-password"
                                    data-testid="admin-login-password"
                                    type={showPwd ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••"
                                    autoComplete="current-password"
                                    required
                                    className="pl-10 pr-10 h-12 rounded-none border-[#0F1F38]/25 bg-white focus-visible:ring-[#0F1F38] focus-visible:ring-offset-0"
                                />
                                <button
                                    type="button"
                                    data-testid="admin-login-toggle-password"
                                    onClick={() => setShowPwd((s) => !s)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0F1F38]/50 hover:text-[#0F1F38]"
                                >
                                    {showPwd ? (
                                        <EyeOff size={16} />
                                    ) : (
                                        <Eye size={16} />
                                    )}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div
                                data-testid="admin-login-error"
                                className="text-sm text-red-700 border-l-2 border-red-700 pl-3"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            data-testid="admin-login-submit"
                            disabled={submitting}
                            className="w-full h-12 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors duration-200 disabled:opacity-60"
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
}
